import { cn } from "@faire/prototype-playground/lib/utils";
import {
  Plus,
  Minus,
  Check,
  X,
  WarningCircle,
  DotsThree,
  PencilSimple,
  Eye,
  EyeSlash,
  Copy,
  Trash,
  Package,
  Info,
} from "@phosphor-icons/react";
import React, { useState, useMemo, useRef, useEffect } from "react";

import {
  Button,
  Checkbox,
  ExportTextButton,
  IconButton,
  Menu,
  MenuTrigger,
  MenuItem,
  Tag,
  Tooltip,
} from "../../components/design-system";
import type {
  TableConfig,
  SlateTableRow,
  RowAction,
  Column,
} from "../../types/table";
import { MOCK_ROWS, createPlaceholderRow } from "../../types/table";

// ─── Toolbar truck icon (FBF) ─────────────────────────────────────────────────

function TruckIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0 }}
    >
      <path
        d="M21 18C21 18.5304 20.7893 19.0391 20.4142 19.4142C20.0391 19.7893 19.5304 20 19 20C18.4696 20 17.9609 19.7893 17.5858 19.4142C17.2107 19.0391 17 18.5304 17 18C17 17.4696 17.2107 16.9609 17.5858 16.5858C17.9609 16.2107 18.4696 16 19 16C19.5304 16 20.0391 16.2107 20.4142 16.5858C20.7893 16.9609 21 17.4696 21 18V18Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7 18C7 18.5304 6.78929 19.0391 6.41421 19.4142C6.03914 19.7893 5.53043 20 5 20C4.46957 20 3.96086 19.7893 3.58579 19.4142C3.21071 19.0391 3 18.5304 3 18C3 17.4696 3.21071 16.9609 3.58579 16.5858C3.96086 16.2107 4.46957 16 5 16C5.53043 16 6.03914 16.2107 6.41421 16.5858C6.78929 16.9609 7 17.4696 7 18Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M21 18H22.5C22.7652 18 23.0196 17.8946 23.2071 17.7071C23.3946 17.5196 23.5 17.2652 23.5 17V13.015C23.5002 12.8556 23.4622 12.6985 23.3894 12.5567C23.3165 12.4149 23.2107 12.2926 23.081 12.2L20 11L19.189 6.757C19.1348 6.54075 19.01 6.34881 18.8342 6.21166C18.6585 6.0745 18.4419 6.00001 18.219 6H14.5V5C14.5 4.73478 14.3946 4.48043 14.2071 4.29289C14.0196 4.10536 13.7652 4 13.5 4H1.5C1.23478 4 0.980429 4.10536 0.792892 4.29289C0.605356 4.48043 0.5 4.73478 0.5 5V17C0.5 17.2652 0.605356 17.5196 0.792892 17.7071C0.980429 17.8946 1.23478 18 1.5 18H3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17 18H7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.5 6V16"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M23.5 15H22"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M20 11H14.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── Sort indicator ───────────────────────────────────────────────────────────

/** Column sort direction for sortable header indicators */
export type TableSortDirection = "asc" | "desc";

const ColumnSortIcon = ({ direction }: { direction: TableSortDirection }) => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    {direction === "asc" ? (
      <path
        d="M6 2.5L6 9.5M6 9.5L3 6.5M6 9.5L9 6.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ) : (
      <path
        d="M6 9.5L6 2.5M6 2.5L3 5.5M6 2.5L9 5.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )}
  </svg>
);

/** Custom cell renderer — return a ReactNode to override, or undefined to use the default */
export type CustomCellRenderer = (
  cellType: string,
  value: string,
  row: SlateTableRow,
  column: Column
) => React.ReactNode | undefined;

interface SlateTableProps {
  config: TableConfig;
  rows?: SlateTableRow[];
  editable?: boolean;
  /** Show an animated gradient border around the table */
  highlighted?: boolean;
  /** Override cell rendering for specific types or values */
  customCellRenderer?: CustomCellRenderer;
  /** Make the table header sticky when scrolling. Sticks at 40px from viewport top. */
  stickyHeader?: boolean;
  /** When true, show a green toolbar above the table header when rows are selected (e.g. "X products selected" + actions) */
  selectionToolbar?: boolean;
  /** Label for the selection count (e.g. "products" for "5 products selected") */
  selectionToolbarLabel?: string;
  /** FBF filter value: "all" | "needs-setup" | "configured" — controls Setup for FBF and Create shipment link visibility */
  selectionToolbarFbfFilter?: "all" | "needs-setup" | "configured";
  /** Called when Bulk edit is clicked in the selection toolbar */
  onBulkEditClick?: () => void;
  /** Column ids that are sortable — only these columns get the clickable button treatment */
  sortableColumnIds?: string[];
  /** Called when a sortable column header is clicked */
  onColumnHeaderClick?: (colId: string) => void;
  /** Column id that is currently sorted — shows a sort indicator in the header */
  sortedColumnId?: string;
  /** Sort direction for the active sorted column */
  sortedColumnDirection?: TableSortDirection;
  onConfigChange?: (config: TableConfig) => void;
  onRowSelect?: (rowId: string, selected: boolean) => void;
  onActionClick?: (action: RowAction, rowId: string) => void;
  /** When true for a row, the row shows pointer cursor and receives click events */
  isRowClickable?: (rowId: string) => boolean;
  /** Called when a clickable row is clicked (any cell except stopped interactions) */
  onRowClick?: (rowId: string) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SlateTable({
  config,
  rows,
  editable = false,
  highlighted = false,
  customCellRenderer,
  stickyHeader = false,
  selectionToolbar = false,
  selectionToolbarLabel = "products",
  selectionToolbarFbfFilter,
  onBulkEditClick,
  sortableColumnIds,
  onColumnHeaderClick,
  sortedColumnId,
  sortedColumnDirection = "asc",
  onConfigChange,
  onRowSelect,
  onActionClick,
  isRowClickable,
  onRowClick,
}: SlateTableProps) {
  const baseRows = rows ?? MOCK_ROWS;
  // When parent passes explicit rows, never pad — use actual row count so filtered data doesn't show placeholder rows
  const effectiveRowCount =
    rows != null && rows.length < config.rowCount
      ? rows.length
      : config.rowCount;
  const allRows = useMemo(() => {
    if (effectiveRowCount <= baseRows.length) return baseRows;
    // Pad with placeholder rows for counts beyond available data
    const extra = Array.from(
      { length: effectiveRowCount - baseRows.length },
      (_, i) => createPlaceholderRow(`rg-${baseRows.length + i + 1}`)
    );
    return [...baseRows, ...extra];
  }, [baseRows, effectiveRowCount]);
  const data =
    effectiveRowCount > 0 ? allRows.slice(0, effectiveRowCount) : allRows;

  // ── Sticky header detection ────────────────────────────────────────────────

  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    if (!stickyHeader) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(!entry.isIntersecting),
      { rootMargin: "-41px 0px 0px 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [stickyHeader]);

  // ── Internal interactive state ──────────────────────────────────────────────

  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [checkboxValues, setCheckboxValues] = useState<Record<string, boolean>>(
    {}
  );
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [editingCell, setEditingCell] = useState<{
    colId: string;
    rowId: string;
  } | null>(null);

  // ── Selection state ─────────────────────────────────────────────────────────

  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  const allSelected = data.every((r) => selectedRows.has(r.id));
  const someSelected = data.some((r) => selectedRows.has(r.id)) && !allSelected;

  function toggleSelectAll() {
    if (allSelected) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(data.map((r) => r.id)));
    }
  }

  function toggleRow(id: string) {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      const wasSelected = next.has(id);
      wasSelected ? next.delete(id) : next.add(id);
      onRowSelect?.(id, !wasSelected);
      return next;
    });
  }

  // ── Derived values ──────────────────────────────────────────────────────────

  const {
    columns,
    cellValues,
    imageUrlValues,
    subtextValues,
    rowsSelectable,
    rowActionsEnabled,
    rowActionItems,
  } = config;

  const inlineActions = useMemo(
    () => rowActionItems.filter((a) => a.display === "inline"),
    [rowActionItems]
  );
  const overflowActions = useMemo(
    () => rowActionItems.filter((a) => a.display === "overflow"),
    [rowActionItems]
  );
  const hasAnyActions = rowActionItems.length > 0;

  // ── Cell value helpers (mutate config when editable + onConfigChange) ──────

  function updateCellValue(ck: string, value: string) {
    if (onConfigChange) {
      onConfigChange({ ...config, cellValues: { ...cellValues, [ck]: value } });
    }
  }

  function updateImageUrlValue(ck: string, value: string) {
    if (onConfigChange) {
      onConfigChange({
        ...config,
        imageUrlValues: { ...imageUrlValues, [ck]: value },
      });
    }
  }

  function updateSubtextValue(ck: string, value: string) {
    if (onConfigChange) {
      onConfigChange({
        ...config,
        subtextValues: { ...subtextValues, [ck]: value },
      });
    }
  }

  // ── Cell renderer ─────────────────────────────────────────────────────────

  function renderCell(col: Column, row: SlateTableRow) {
    const ck = `${col.id}--${row.id}`;
    const isEditing =
      editable &&
      editingCell?.colId === col.id &&
      editingCell?.rowId === row.id;

    // Check for custom cell renderer override (skip during edit mode)
    if (customCellRenderer && !isEditing) {
      const displayValue =
        cellValues[ck] ??
        (row[col.cellType as keyof SlateTableRow] as string) ??
        "";
      const custom = customCellRenderer(
        col.cellType,
        String(displayValue),
        row,
        col
      );
      if (custom !== undefined) return custom;
    }

    const startEditing = editable
      ? (e: React.MouseEvent) => {
          e.stopPropagation();
          setEditingCell({ colId: col.id, rowId: row.id });
        }
      : undefined;

    switch (col.cellType) {
      case "text":
        return isEditing ? (
          <input
            autoFocus
            type="text"
            value={cellValues[ck] ?? row.text}
            onChange={(e) => updateCellValue(ck, e.target.value)}
            onBlur={() => setEditingCell(null)}
            onClick={(e) => e.stopPropagation()}
            className="w-full rounded border border-[#275ec5] px-2 py-0.5 text-sm outline-none"
            style={{ color: "var(--slate-color-text-primary)" }}
          />
        ) : (
          <span
            className="block min-h-[1.5em] min-w-[2rem] truncate"
            style={{
              fontFamily: "var(--slate-typography-family-sans)",
              fontSize: "var(--slate-typography-font-size-200)",
              fontWeight: col.bold
                ? "var(--slate-typography-weight-medium)"
                : "var(--slate-typography-weight-regular)",
              color: "var(--slate-color-text-primary)",
              cursor: editable ? "text" : undefined,
            }}
            onClick={startEditing}
          >
            {cellValues[ck] ?? row.text}
          </span>
        );

      case "imageAndText": {
        const imgUrl = imageUrlValues[ck] ?? row.imageAndText.url;
        const imgName = cellValues[ck] ?? row.imageAndText.name;
        const imgSubtext = subtextValues[ck] ?? row.imageAndText.subtext ?? "";
        return isEditing ? (
          <div
            className="flex flex-col gap-1"
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node))
                setEditingCell(null);
            }}
          >
            <input
              autoFocus
              placeholder="Image URL"
              type="text"
              value={imageUrlValues[ck] ?? row.imageAndText.url}
              onChange={(e) => updateImageUrlValue(ck, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="w-full rounded border border-[#275ec5] px-2 py-0.5 text-xs outline-none"
              style={{ color: "var(--slate-color-text-primary)" }}
            />
            <input
              placeholder="Name"
              type="text"
              value={cellValues[ck] ?? row.imageAndText.name}
              onChange={(e) => updateCellValue(ck, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="w-full rounded border border-[#275ec5] px-2 py-0.5 text-xs outline-none"
              style={{ color: "var(--slate-color-text-primary)" }}
            />
            <input
              placeholder="Subtext (optional)"
              type="text"
              value={subtextValues[ck] ?? row.imageAndText.subtext ?? ""}
              onChange={(e) => updateSubtextValue(ck, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="w-full rounded border border-[#275ec5] px-2 py-0.5 text-xs outline-none"
              style={{ color: "var(--slate-color-text-subdued)" }}
            />
          </div>
        ) : !imgName && !imgSubtext ? (
          <img
            src={imgUrl}
            alt=""
            className="h-9 w-9 flex-shrink-0 rounded object-cover"
            style={{ cursor: editable ? "pointer" : undefined }}
            onClick={startEditing}
          />
        ) : (
          <div
            className="flex min-h-[1.5em] min-w-0 min-w-[2rem] items-center gap-4"
            style={{ cursor: editable ? "text" : undefined }}
            onClick={startEditing}
          >
            <img
              src={imgUrl}
              alt={imgName}
              className="h-9 w-9 flex-shrink-0 rounded object-cover"
            />
            <div className="flex min-w-0 flex-col">
              <span
                className="truncate"
                style={{
                  fontFamily: "var(--slate-typography-family-sans)",
                  fontSize: "var(--slate-typography-font-size-200)",
                  fontWeight: col.bold
                    ? "var(--slate-typography-weight-medium)"
                    : "var(--slate-typography-weight-regular)",
                  color: "var(--slate-color-text-primary)",
                }}
              >
                {imgName}
              </span>
              {imgSubtext && (
                <span
                  className="truncate"
                  style={{
                    fontFamily: "var(--slate-typography-family-sans)",
                    fontSize: "var(--slate-typography-font-size-100)",
                    fontWeight: "var(--slate-typography-weight-regular)",
                    color: "var(--slate-color-text-subdued)",
                  }}
                >
                  {imgSubtext}
                </span>
              )}
            </div>
          </div>
        );
      }

      case "tag":
        return isEditing ? (
          <input
            autoFocus
            type="text"
            value={cellValues[ck] ?? row.tag}
            onChange={(e) => updateCellValue(ck, e.target.value)}
            onBlur={() => setEditingCell(null)}
            onClick={(e) => e.stopPropagation()}
            className="w-full rounded border border-[var(--slate-color-action-border-focus-ring)] px-2 py-0.5 outline-none"
            style={{
              fontFamily: "var(--slate-typography-family-sans)",
              fontSize: "var(--slate-typography-font-size-200)",
              color: "var(--slate-color-text-primary)",
            }}
          />
        ) : (
          <span
            className="inline-block min-h-[1.5em] min-w-[2rem]"
            style={{ cursor: editable ? "text" : undefined }}
            onClick={startEditing}
          >
            <Tag label={cellValues[ck] ?? row.tag} />
          </span>
        );

      case "tagGroup": {
        const rawTags = cellValues[ck];
        const tags =
          rawTags != null
            ? rawTags
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : row.tagGroup;
        return isEditing ? (
          <input
            autoFocus
            type="text"
            value={cellValues[ck] ?? row.tagGroup.join(", ")}
            onChange={(e) => updateCellValue(ck, e.target.value)}
            onBlur={() => setEditingCell(null)}
            onClick={(e) => e.stopPropagation()}
            className="w-full rounded border border-[#275ec5] px-2 py-0.5 text-sm outline-none"
            style={{ color: "var(--slate-color-text-primary)" }}
            placeholder="Tag1, Tag2"
          />
        ) : (
          <div
            className="flex min-h-[1.5em] min-w-[2rem] flex-wrap gap-1"
            style={{ cursor: editable ? "text" : undefined }}
            onClick={startEditing}
          >
            {tags.map((t) => (
              <Tag key={t} label={t} />
            ))}
          </div>
        );
      }

      case "input":
        return (
          <input
            type="text"
            value={inputValues[ck] ?? row.input}
            onChange={(e) =>
              setInputValues((prev) => ({ ...prev, [ck]: e.target.value }))
            }
            onClick={(e) => e.stopPropagation()}
            className="w-full rounded border border-[var(--slate-color-border-muted)] px-2 py-1 text-xs outline-none focus:border-[#275ec5]"
            style={{
              color: "var(--slate-color-text-primary)",
              background: "#fff",
            }}
          />
        );

      case "quantityPicker": {
        const qty = quantities[ck] ?? row.quantityPicker;
        return (
          <div
            className="flex items-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            <IconButton
              Icon={() => <Minus size={12} />}
              variant="secondary"
              shape="square"
              size="xxSmall"
              onClick={() =>
                setQuantities((prev) => ({
                  ...prev,
                  [ck]: Math.max(0, (prev[ck] ?? row.quantityPicker) - 1),
                }))
              }
              aria-label="Decrease quantity"
            />
            <span
              className="w-6 text-center"
              style={{
                fontFamily: "var(--slate-typography-family-sans)",
                fontSize: "var(--slate-typography-font-size-100)",
                color: "var(--slate-color-text-primary)",
              }}
            >
              {qty}
            </span>
            <IconButton
              Icon={() => <Plus size={12} />}
              variant="secondary"
              shape="square"
              size="xxSmall"
              onClick={() =>
                setQuantities((prev) => ({
                  ...prev,
                  [ck]: (prev[ck] ?? row.quantityPicker) + 1,
                }))
              }
              aria-label="Increase quantity"
            />
          </div>
        );
      }

      case "checkbox":
        return (
          <span onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={checkboxValues[ck] ?? row.checkbox}
              onChange={() =>
                setCheckboxValues((prev) => ({
                  ...prev,
                  [ck]: !(prev[ck] ?? row.checkbox),
                }))
              }
              aria-label="Row checkbox"
            />
          </span>
        );

      case "icon": {
        const iconVal = cellValues[ck] ?? row.icon;
        const iconDisplay =
          iconVal === "check" ? (
            <Check size={16} color="var(--slate-color-message-icon-success)" />
          ) : iconVal === "x" ? (
            <X size={16} color="var(--slate-color-icon-subdued)" />
          ) : iconVal === "alertCircle" ? (
            <WarningCircle
              size={16}
              color="var(--slate-color-message-icon-warning)"
            />
          ) : null;

        return isEditing ? (
          <select
            autoFocus
            value={cellValues[ck] ?? row.icon}
            onChange={(e) => {
              updateCellValue(ck, e.target.value);
              setEditingCell(null);
            }}
            onBlur={() => setEditingCell(null)}
            onClick={(e) => e.stopPropagation()}
            className="bg-fs-surface-primary rounded border border-[#275ec5] px-1 py-0.5 text-xs outline-none"
            style={{ color: "var(--slate-color-text-primary)" }}
          >
            <option value="check">check</option>
            <option value="x">x</option>
            <option value="alertCircle">alertCircle</option>
          </select>
        ) : (
          <span
            className="inline-block min-h-[1.5em] min-w-[2rem]"
            style={{ cursor: editable ? "pointer" : undefined }}
            onClick={startEditing}
          >
            {iconDisplay}
          </span>
        );
      }

      case "button":
        return isEditing ? (
          <input
            autoFocus
            type="text"
            value={cellValues[ck] ?? row.button}
            onChange={(e) => updateCellValue(ck, e.target.value)}
            onBlur={() => setEditingCell(null)}
            onClick={(e) => e.stopPropagation()}
            className="w-full rounded border border-[var(--slate-color-action-border-focus-ring)] px-2 py-0.5 outline-none"
            style={{
              fontFamily: "var(--slate-typography-family-sans)",
              fontSize: "var(--slate-typography-font-size-200)",
              color: "var(--slate-color-text-primary)",
            }}
          />
        ) : (
          <Button variant="secondary" size="xSmall" onClick={startEditing}>
            {cellValues[ck] ?? row.button}
          </Button>
        );

      case "link":
        return isEditing ? (
          <input
            autoFocus
            type="text"
            value={cellValues[ck] ?? row.link}
            onChange={(e) => updateCellValue(ck, e.target.value)}
            onBlur={() => setEditingCell(null)}
            onClick={(e) => e.stopPropagation()}
            className="w-full rounded border border-[var(--slate-color-action-border-focus-ring)] px-2 py-0.5 outline-none"
            style={{
              fontFamily: "var(--slate-typography-family-sans)",
              fontSize: "var(--slate-typography-font-size-200)",
              color: "var(--slate-color-text-primary)",
            }}
          />
        ) : (
          <span className="inline-block min-h-[1.5em] min-w-[2rem]">
            <Button variant="plain" onClick={startEditing}>
              {(cellValues[ck] ?? row.link) || "\u00A0"}
            </Button>
          </span>
        );

      default:
        return null;
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="relative">
      {highlighted && (
        <style>{`
          @property --slate-border-angle {
            syntax: "<angle>";
            initial-value: 0deg;
            inherits: false;
          }
          @keyframes slate-border-race {
            to { --slate-border-angle: 360deg; }
          }
        `}</style>
      )}
      {highlighted && (
        <div
          className="pointer-events-none absolute"
          style={{
            inset: -2,
            borderRadius: 8,
            background:
              "conic-gradient(from var(--slate-border-angle, 0deg), transparent 0%, transparent 60%, #a78bfa 75%, #c084fc 82%, #fb923c 92%, transparent 100%)",
            animation:
              "slate-border-race 2.5s cubic-bezier(0.25, 0.1, 0.75, 0.9) infinite",
          }}
        />
      )}
      {stickyHeader && !highlighted && (
        <div ref={sentinelRef} className="h-0" />
      )}
      {stickyHeader && !highlighted && (
        <div
          className={cn(
            "after:bg-fs-surface-primary sticky top-0 z-10 h-0 after:absolute after:top-0 after:right-0 after:left-0 after:h-[40px] after:content-['']",
            isStuck ? "" : "after:hidden"
          )}
        />
      )}
      <div
        className="relative"
        style={{
          background: highlighted ? "#fff" : undefined,
          borderRadius: highlighted ? 6 : 0,
          overflow: highlighted ? "hidden" : undefined,
        }}
      >
        {selectionToolbar && rowsSelectable && selectedRows.size > 0 && (
          <div
            className="flex shrink-0 flex-nowrap items-center justify-between gap-4 overflow-x-auto px-4 py-2"
            style={{
              background: "#E8F1E4",
              fontFamily: "var(--slate-typography-family-sans)",
              fontSize: "var(--slate-typography-font-size-200)",
              color: "var(--slate-color-text-primary)",
            }}
          >
            <div className="flex shrink-0 flex-nowrap items-center gap-2">
              <span className="whitespace-nowrap">
                {selectedRows.size} {selectionToolbarLabel} selected
              </span>
              <button
                type="button"
                onClick={() => setSelectedRows(new Set())}
                className="inline-flex items-center rounded underline hover:no-underline focus:ring-2 focus:ring-[#275ec5] focus:ring-offset-1 focus:outline-none"
                style={{
                  paddingLeft: 12,
                  paddingRight: 12,
                  minHeight: 28,
                  fontFamily: "var(--slate-typography-family-sans)",
                  fontSize: "var(--slate-typography-font-size-200)",
                  lineHeight: "var(--slate-typography-line-height-200)",
                  color: "var(--slate-color-text-primary)",
                }}
              >
                Clear selection
              </button>
            </div>
            <div className="flex shrink-0 flex-nowrap items-center gap-1">
              {selectionToolbarFbfFilter &&
                (() => {
                  const EMDASH = "—";
                  const selectedNeedsSetup = data.filter(
                    (r) =>
                      selectedRows.has(r.id) &&
                      r.tagGroup?.[0] === "Needs setup"
                  ).length;
                  const selectedConfigured = data.filter(
                    (r) =>
                      selectedRows.has(r.id) && r.tagGroup?.[0] === "Configured"
                  ).length;
                  const hasEmDashInSelection = data.some(
                    (r) => selectedRows.has(r.id) && r.tagGroup?.[0] === EMDASH
                  );
                  const showSetupForFbf =
                    selectedNeedsSetup > 0 || selectedConfigured > 0;
                  const showCreateShipment = selectedConfigured > 0;
                  const linkBaseStyle = {
                    paddingLeft: 12,
                    paddingRight: 12,
                    minHeight: 28,
                    fontFamily: "var(--slate-typography-family-sans)",
                    fontSize: "var(--slate-typography-font-size-200)",
                    lineHeight: "var(--slate-typography-line-height-200)",
                    color: hasEmDashInSelection
                      ? "var(--slate-color-text-subdued)"
                      : "var(--slate-color-text-primary)",
                    cursor: hasEmDashInSelection ? "not-allowed" : "pointer",
                  } as const;
                  return (
                    <>
                      {showSetupForFbf && (
                        <button
                          type="button"
                          onClick={() => {}}
                          disabled={hasEmDashInSelection}
                          className="inline-flex items-center gap-2 rounded underline hover:no-underline focus:ring-2 focus:ring-[#275ec5] focus:ring-offset-1 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                          style={linkBaseStyle}
                        >
                          <TruckIcon size={16} />
                          Setup for FBF ({selectedNeedsSetup})
                        </button>
                      )}
                      {showCreateShipment && (
                        <button
                          type="button"
                          onClick={() => {}}
                          disabled={hasEmDashInSelection}
                          className="inline-flex items-center gap-2 rounded underline hover:no-underline focus:ring-2 focus:ring-[#275ec5] focus:ring-offset-1 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                          style={linkBaseStyle}
                        >
                          <Package size={16} weight="regular" />
                          Create shipment ({selectedConfigured})
                        </button>
                      )}
                    </>
                  );
                })()}
              <Button
                variant="plain"
                size="xSmall"
                icon={<PencilSimple size={16} weight="regular" />}
                iconPosition="start"
                aria-label="Bulk edit"
                onClick={() => onBulkEditClick?.()}
                style={{
                  paddingLeft: 12,
                  paddingRight: 12,
                  minHeight: 28,
                  fontSize: "var(--slate-typography-font-size-200)",
                  lineHeight: "var(--slate-typography-line-height-200)",
                }}
              >
                Bulk edit
              </Button>
              <ExportTextButton
                className="min-h-[28px] py-0"
                labelClassName="text-[length:var(--slate-typography-font-size-200)] leading-[var(--slate-typography-line-height-200)]"
              />
              <Button
                variant="plain"
                size="xSmall"
                icon={<Eye size={16} weight="regular" />}
                iconPosition="start"
                aria-label="Publish"
                onClick={() => {}}
                style={{
                  paddingLeft: 12,
                  paddingRight: 12,
                  minHeight: 28,
                  fontSize: "var(--slate-typography-font-size-200)",
                  lineHeight: "var(--slate-typography-line-height-200)",
                }}
              >
                Publish
              </Button>
              <Button
                variant="plain"
                size="xSmall"
                icon={<EyeSlash size={16} weight="regular" />}
                iconPosition="start"
                aria-label="Unpublish"
                onClick={() => {}}
                style={{
                  paddingLeft: 12,
                  paddingRight: 12,
                  minHeight: 28,
                  fontSize: "var(--slate-typography-font-size-200)",
                  lineHeight: "var(--slate-typography-line-height-200)",
                }}
              >
                Unpublish
              </Button>
              <Button
                variant="plain"
                size="xSmall"
                icon={<Copy size={16} weight="regular" />}
                iconPosition="start"
                aria-label="Duplicate"
                onClick={() => {}}
                style={{
                  paddingLeft: 12,
                  paddingRight: 12,
                  minHeight: 28,
                  fontSize: "var(--slate-typography-font-size-200)",
                  lineHeight: "var(--slate-typography-line-height-200)",
                }}
              >
                Duplicate
              </Button>
              <Button
                variant="plain"
                size="xSmall"
                icon={<Trash size={16} weight="regular" />}
                iconPosition="start"
                aria-label="Delete"
                onClick={() => {}}
                style={{
                  paddingLeft: 12,
                  paddingRight: 12,
                  minHeight: 28,
                  fontSize: "var(--slate-typography-font-size-200)",
                  lineHeight: "var(--slate-typography-line-height-200)",
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        )}
        <table
          className="border-collapse"
          style={{ minWidth: "max-content", width: "100%" }}
        >
          <thead
            className={
              stickyHeader && !highlighted ? "sticky top-[40px] z-20" : ""
            }
          >
            <tr
              style={{
                height: 32,
                background: "var(--slate-color-surface-tertiary)",
              }}
            >
              {columns.map((col, colIndex) => (
                <th
                  key={col.id}
                  style={{
                    width: col.width,
                    minWidth: col.width,
                    paddingLeft: col.paddingLeft ?? 16,
                    paddingRight: col.paddingRight ?? 16,
                    ...(col.pinned
                      ? {
                          position: "sticky",
                          left: 0,
                          zIndex: 3,
                          background: "var(--slate-color-surface-tertiary)",
                          borderRight:
                            "1px solid var(--slate-color-border-muted)",
                        }
                      : {}),
                  }}
                  className={cn(
                    "border-r-0 py-1 text-left",
                    colIndex === 0 ? "rounded-l" : "",
                    colIndex === columns.length - 1 &&
                      !(rowActionsEnabled && hasAnyActions)
                      ? "rounded-r"
                      : ""
                  )}
                >
                  {rowsSelectable && colIndex === 0 ? (
                    <div className="flex items-center gap-4">
                      <span onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={someSelected ? "indeterminate" : allSelected}
                          onChange={toggleSelectAll}
                          aria-label="Select all rows"
                        />
                      </span>
                      <span
                        style={{
                          fontFamily: "var(--slate-typography-family-sans)",
                          fontSize: "var(--slate-typography-font-size-100)",
                          fontWeight: "var(--slate-typography-weight-medium)",
                          color: "var(--slate-color-text-primary)",
                        }}
                      >
                        {col.label}
                      </span>
                    </div>
                  ) : sortableColumnIds?.includes(col.id) ? (
                    <button
                      type="button"
                      onClick={() => onColumnHeaderClick?.(col.id)}
                      className="flex cursor-pointer items-center gap-1 select-none"
                      style={{
                        fontFamily: "var(--slate-typography-family-sans)",
                        fontSize: "var(--slate-typography-font-size-100)",
                        fontWeight: "var(--slate-typography-weight-medium)",
                        color: "var(--slate-color-text-primary)",
                        background: "none",
                        border: "none",
                        padding: 0,
                      }}
                    >
                      {col.label}
                      <ColumnSortIcon direction={sortedColumnDirection} />
                    </button>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1"
                      style={{
                        fontFamily: "var(--slate-typography-family-sans)",
                        fontSize: "var(--slate-typography-font-size-100)",
                        fontWeight: "var(--slate-typography-weight-medium)",
                        color: "var(--slate-color-text-primary)",
                      }}
                    >
                      {col.label}
                      {col.headerTooltip && (
                        <Tooltip label={col.headerTooltip}>
                          <Info
                            size={14}
                            style={{ color: "var(--slate-color-text-subdued)" }}
                          />
                        </Tooltip>
                      )}
                    </span>
                  )}
                </th>
              ))}

              {/* Row actions header */}
              {rowActionsEnabled && hasAnyActions && (
                <th
                  className="rounded-r px-4 py-1"
                  style={{ width: 1, whiteSpace: "nowrap" }}
                />
              )}
            </tr>
          </thead>

          <tbody>
            {/* 2px spacer between header and first row */}
            <tr aria-hidden style={{ height: 2 }}>
              <td
                colSpan={
                  columns.length + (rowActionsEnabled && hasAnyActions ? 1 : 0)
                }
                style={{
                  padding: 0,
                  background: highlighted ? "#fff" : undefined,
                }}
              />
            </tr>
            {data.map((row, rowIndex) => {
              const rowClickable = isRowClickable?.(row.id) ?? false;
              const rowBackground = selectedRows.has(row.id)
                ? "var(--slate-color-surface-tertiary)"
                : "var(--slate-color-surface-primary)";

              return (
                <tr
                  key={row.id}
                  className={
                    rowClickable
                      ? "cursor-pointer transition-colors"
                      : "transition-colors"
                  }
                  onClick={
                    rowClickable ? () => onRowClick?.(row.id) : undefined
                  }
                  style={{
                    height: 56,
                    borderBottom: "1px solid var(--slate-color-border-muted)",
                    background: rowBackground,
                  }}
                  onMouseEnter={(e) => {
                    if (selectedRows.has(row.id)) return;
                    const rowEl = e.currentTarget as HTMLTableRowElement;
                    rowEl.style.background =
                      "var(--slate-color-surface-secondary)";
                    rowEl
                      .querySelectorAll<HTMLElement>("[data-pinned-cell]")
                      .forEach((cell) => {
                        cell.style.background =
                          "var(--slate-color-surface-secondary)";
                      });
                  }}
                  onMouseLeave={(e) => {
                    if (selectedRows.has(row.id)) return;
                    const rowEl = e.currentTarget as HTMLTableRowElement;
                    rowEl.style.background = "var(--slate-color-surface-primary)";
                    rowEl
                      .querySelectorAll<HTMLElement>("[data-pinned-cell]")
                      .forEach((cell) => {
                        cell.style.background =
                          "var(--slate-color-surface-primary)";
                      });
                  }}
                >
                  {/* Data cells */}
                  {columns.map((col, colIndex) => {
                    const isFirstRow = rowIndex === 0;
                    const isFirstCol = colIndex === 0;
                    const isLastCol =
                      colIndex === columns.length - 1 &&
                      !(rowActionsEnabled && hasAnyActions);
                    return (
                      <td
                        key={col.id}
                        data-pinned-cell={col.pinned ? true : undefined}
                        style={{
                          width: col.width,
                          minWidth: col.width,
                          paddingLeft: col.paddingLeft ?? 16,
                          paddingRight: col.paddingRight ?? 16,
                          ...(isFirstRow && isFirstCol
                            ? { borderTopLeftRadius: 4 }
                            : {}),
                          ...(isFirstRow && isLastCol
                            ? { borderTopRightRadius: 4 }
                            : {}),
                          ...(col.pinned
                            ? {
                                position: "sticky",
                                left: 0,
                                zIndex: 2,
                                background: rowBackground,
                                borderRight:
                                  "1px solid var(--slate-color-border-muted)",
                              }
                            : {}),
                        }}
                        className="border-r-0 py-2"
                      >
                        {rowsSelectable && colIndex === 0 ? (
                          <div
                            className="flex min-w-0 items-center gap-4"
                            style={{
                              gap: col.cellType === "checkbox" ? 0 : 16,
                            }}
                          >
                            <span onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedRows.has(row.id)}
                                onChange={() => toggleRow(row.id)}
                                aria-label="Select row"
                              />
                            </span>
                            {col.cellType !== "checkbox" && (
                              <div className="min-w-0 flex-1">
                                {renderCell(col, row)}
                              </div>
                            )}
                          </div>
                        ) : (
                          renderCell(col, row)
                        )}
                      </td>
                    );
                  })}

                  {/* Row actions cell */}
                  {rowActionsEnabled && hasAnyActions && (
                    <td
                      className="py-2 pr-1 pl-2"
                      style={{
                        whiteSpace: "nowrap",
                        ...(rowIndex === 0 ? { borderTopRightRadius: 4 } : {}),
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-end gap-1">
                        {inlineActions.map((action) => (
                          <Button
                            key={action.id}
                            variant={action.variant}
                            size="xSmall"
                            onClick={() => onActionClick?.(action, row.id)}
                          >
                            {action.label}
                          </Button>
                        ))}
                        {overflowActions.length > 0 && (
                          <Menu aria-label="Row actions" alignment="end">
                            <MenuTrigger>
                              <IconButton
                                Icon={() => (
                                  <DotsThree size={20} weight="regular" />
                                )}
                                aria-label="Open row actions menu"
                                size="xSmall"
                                variant="tertiary"
                              />
                            </MenuTrigger>
                            {overflowActions.map((action) => (
                              <MenuItem
                                key={action.id}
                                label={action.label}
                                onClick={() => onActionClick?.(action, row.id)}
                              />
                            ))}
                          </Menu>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
